import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { 
  Rule, 
  RuleFilters, 
  RULE_CATEGORIES 
} from '../../types/rules';

interface RulesListProps {
  rules: Rule[];
  loading: boolean;
  onEdit: (rule: Rule) => void;
  onDelete: (id: string) => Promise<void>;
  onTest: (rule: Rule) => void;
  onViewHistory: (rule: Rule) => void;
  onCreate: () => void;
  onRefresh: () => void;
  filters: RuleFilters;
  onFiltersChange: (filters: RuleFilters) => void;
}

const RulesList: React.FC<RulesListProps> = ({
  rules,
  loading,
  onEdit,
  onDelete,
  onTest,
  onViewHistory,
  onCreate,
  onRefresh,
  filters,
  onFiltersChange
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<Rule | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter rules based on search term and filters
  const filteredRules = rules.filter(rule => {
    const matchesSearch = !searchTerm || 
      rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !filters.category || rule.category === filters.category;
    const matchesActive = filters.isActive === undefined || rule.isActive === filters.isActive;
    const matchesTag = !filters.tag || rule.tags?.includes(filters.tag);

    return matchesSearch && matchesCategory && matchesActive && matchesTag;
  });

  // Paginated rules
  const paginatedRules = filteredRules.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (rule: Rule) => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (ruleToDelete) {
      try {
        await onDelete(ruleToDelete.id);
        setDeleteDialogOpen(false);
        setRuleToDelete(null);
        onRefresh();
      } catch (error) {
        console.error('Error deleting rule:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  };

  const getCategoryColor = (category: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'Credit Scoring': 'primary',
      'Risk Assessment': 'warning',
      'Loan Approval': 'success',
      'Document Validation': 'info',
      'Pricing': 'secondary',
      'Compliance': 'error',
      'Notification': 'default',
      'Other': 'default'
    };
    return colors[category] || 'default';
  };

  const getPriorityColor = (priority: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (priority <= 10) return 'error';
    if (priority <= 30) return 'warning';
    if (priority <= 60) return 'info';
    return 'default';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Rules
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
        >
          Create Rule
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              label="Search rules"
              placeholder="Search rules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ flex: 1 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                label="Category"
                value={filters.category || ''}
                onChange={(e) => onFiltersChange({ ...filters, category: e.target.value || undefined })}
              >
                <MenuItem value="">All</MenuItem>
                {RULE_CATEGORIES.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                label="Status"
                value={filters.isActive === undefined ? '' : filters.isActive.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  onFiltersChange({ 
                    ...filters, 
                    isActive: value === '' ? undefined : value === 'true' 
                  });
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              Filters
            </Button>
          </Stack>

          {filtersOpen && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <TextField
                size="small"
                label="Tag"
                value={filters.tag || ''}
                onChange={(e) => onFiltersChange({ ...filters, tag: e.target.value || undefined })}
                placeholder="Filter by tag"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Triggers</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading rules...
                  </TableCell>
                </TableRow>
              ) : paginatedRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No rules found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRules.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                          {rule.name}
                        </Typography>
                        {rule.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {rule.description.length > 100 
                              ? `${rule.description.substring(0, 100)}...`
                              : rule.description
                            }
                          </Typography>
                        )}
                        {rule.tags && rule.tags.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {rule.tags.slice(0, 3).map(tag => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                            {rule.tags.length > 3 && (
                              <Chip
                                label={`+${rule.tags.length - 3} more`}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.category}
                        color={getCategoryColor(rule.category)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.priority}
                        color={getPriorityColor(rule.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.isActive ? 'Active' : 'Inactive'}
                        color={rule.isActive ? 'success' : 'default'}
                        size="small"
                        variant={rule.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {rule.triggers.slice(0, 2).map(trigger => (
                          <Chip
                            key={trigger}
                            label={trigger.replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        ))}
                        {rule.triggers.length > 2 && (
                          <Tooltip title={rule.triggers.slice(2).join(', ')}>
                            <Chip
                              label={`+${rule.triggers.length - 2}`}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rule.version}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(rule.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Test Rule">
                          <IconButton 
                            size="small" 
                            onClick={() => onTest(rule)}
                            aria-label="Test Rule"
                          >
                            <PlayIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View History">
                          <IconButton 
                            size="small" 
                            onClick={() => onViewHistory(rule)}
                            aria-label="View History"
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Rule">
                          <IconButton 
                            size="small" 
                            onClick={() => onEdit(rule)}
                            aria-label="Edit Rule"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Rule">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(rule)}
                            color="error"
                            aria-label="Delete Rule"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRules.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Rule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rule "{ruleToDelete?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RulesList;